import React, { useState, useEffect } from 'react';
import { StatusBar, Text, TouchableOpacity } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator, CardStyleInterpolators } from '@react-navigation/stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import 'react-native-gesture-handler';
import screenGroups from './real-data.json';
import { styles, headerTintColor } from './components/AppStyles';
import SearchScreen from './SearchScreen';
import HomeScreen from './HomeScreen';
import GroupScreen from './GroupScreen';
import { CounterRatiosContext } from './CounterRatiosContext';
import PercentageScreen from './PercentageScreen';
import { getIsResetButtonPressed, setIsResetButtonPressedValue } from './ResetButtonState';

const Stack = createStackNavigator();

const fadeConfig = {
  cardStyleInterpolator: CardStyleInterpolators.forFadeFromBottomAndroid,
};

const App = () => {
  // Moved the state up from HomeScreen to App
  const [counterRatios, setCounterRatios] = useState({});
  const [isResetButtonPressed, setIsResetButtonPressed] = useState(false); // Flag to track reset button press
  // Define a function to set the isResetButtonPressed state
  const setIsResetButtonPressedValue = (value) => {
    setIsResetButtonPressed(value);
  };
  useEffect(() => {
    const loadCounterRatiosFromStorage = async () => {
      try {
        const storedCounterRatios = await AsyncStorage.getItem('counterRatios');
        if (storedCounterRatios !== null) {
          const parsedData = JSON.parse(storedCounterRatios);
          
          // Merge the stored data and current data to ensure all decks are present
          const mergedData = { ...counterRatios, ...parsedData };
          setCounterRatios(mergedData);
        } else {
          await AsyncStorage.setItem('counterRatios', JSON.stringify(counterRatios));
        }
      } catch (error) {
        //console.error("Failed to load counter ratios from storage:", error);
        // Retry mechanism (try 2 more times, then give up)
        setTimeout(() => {
          loadCounterRatiosFromStorage();
        }, 1000);
      }
    };
    loadCounterRatiosFromStorage();
  }, []);  

  useEffect(() => {
    const saveCounterRatiosToStorage = async () => {
      // Check if counterRatios is empty or null, and the reset button was not pressed
      if (Object.keys(counterRatios).length === 0 && !getIsResetButtonPressed()) {
        //console.log("CounterRatios is empty. Skipping save to storage.");
        return;
      }
  
      try {
        // Backup existing data before overwriting
        const currentData = await AsyncStorage.getItem('counterRatios');
        if (currentData !== null) {
          await AsyncStorage.setItem('counterRatios_backup', currentData);
        }
  
        // Save the current state
        await AsyncStorage.setItem('counterRatios', JSON.stringify(counterRatios));
      } catch (error) {
        //console.error("Failed to save counter ratios to storage:", error);
        // Retry mechanism (try 2 more times, then give up)
        setTimeout(() => {
          saveCounterRatiosToStorage();
        }, 1000);
      }
    };
  
    // Check if counterRatios is empty or null before saving
    if (Object.keys(counterRatios).length !== 0) {
      // if(isResetButtonPressed) {
      //   console.log('Restting all data. Saving to storage...');
      // }
      // else {
      //   console.log('Detected change in counterRatios. Saving to storage...');
      // }
      saveCounterRatiosToStorage();
    }
  
    return () => {
      // This cleanup function will run when the component unmounts
      // Reset the flag when the component unmounts
      setIsResetButtonPressedValue(false);
    };
  }, [counterRatios]);  

  const horizontalAnimation = (direction = 1) => {
    return {
      cardStyleInterpolator: ({ current, layouts }) => {
        return {
          cardStyle: {
            transform: [
              {
                translateX: current.progress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [layouts.screen.width * direction, 0],
                }),
              },
            ],
          },
        };
      },
    };
  };  

  return (
    <CounterRatiosContext.Provider value={{ counterRatios, setCounterRatios }}>
      <StatusBar
        barStyle="light-content" backgroundColor="#011f41"
      />
      <NavigationContainer>
        <Stack.Navigator 
          initialRouteName="Home"
          options={{ gestureEnabled: false }}
          screenOptions={(props) => {
            const { transition } = props.route.params || {};
            let animationConfig;
            if (transition === 'slide-left') {
              animationConfig = horizontalAnimation(-1);
            } else if (transition === 'slide-right') {
              animationConfig = horizontalAnimation(1);
            } else {
              animationConfig = fadeConfig;
            }
          
            // Check if the current screen is a GroupScreen
            const isGroupScreen = screenGroups.some(group => group.groupId === props.route.name);
          
            return {
              ...animationConfig,
              headerStyle: styles.header,
              headerTintColor: headerTintColor,
              headerTitleStyle: styles.headerTitle,
              // If it's a GroupScreen, add the "Home" button to the header
              headerRight: isGroupScreen ? () => (
                <TouchableOpacity onPress={() => props.navigation.navigate('Home')} style={styles.headerButton}>
                  <Text style={styles.headerButtonText}>Home</Text>
                </TouchableOpacity>
              ) : null,
            };
          }}          
        >
          <Stack.Screen 
            name="Home" 
            component={HomeScreen}
            options={{ title: 'Home', gestureEnabled: false }} 
          />
          <Stack.Screen 
            name="Search" 
            component={SearchScreen} 
            options={{ title: 'Search', gestureEnabled: false }} 
          />
      {screenGroups.map((group, index) => (
        <Stack.Screen
          key={group.groupId}
          name={group.groupId}
          component={GroupScreen}
          initialParams={{ screens: group.screens, currentIndex: 0, choice: null, index: index }}
          options={({ route }) => ({
            title: `Deck ${index + 1}`, gestureEnabled: false
          })}
        />
      ))}
        <Stack.Screen name="Percentage" component={PercentageScreen} options={{ gestureEnabled: false }} />
        </Stack.Navigator>
      </NavigationContainer>
    </CounterRatiosContext.Provider>
  );
};

export default App;